import { createContext, useCallback, useContext, useEffect, useState } from 'react'

export interface Me {
  id: string
  name: string
  email: string | null
}

interface AuthState {
  user: Me | null
  /** false, пока /api/me не ответил: чтобы кнопка не мигала «Войти» у залогиненного. */
  ready: boolean
  /** Доступен ли вход через Яндекс: фронт прячет кнопку, пока приложение не зарегистрировано. */
  loginEnabled: boolean
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState>({
  user: null,
  ready: false,
  loginEnabled: false,
  refresh: async () => {},
  logout: async () => {},
})

/**
 * Один вопрос «кто я» на страницу. Сессия — httpOnly-cookie, поэтому здесь
 * только /api/me: токен недоступен из JS и утекать ему неоткуда.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Me | null>(null)
  const [ready, setReady] = useState(false)
  const [loginEnabled, setLoginEnabled] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/me')
      const body = (await res.json()) as { user: Me | null; login: 'yandex' | 'none' }
      setUser(body.user ?? null)
      setLoginEnabled(body.login === 'yandex')
    } catch {
      // Сеть отвалилась — считаем анонимом; при восстановлении следующий заход
      // всё узнает. Выглядеть ошибкой это не должно.
      setUser(null)
    } finally {
      setReady(true)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' })
    } catch {
      /* даже при ошибке локально выходим: cookie переживёт перезагрузку,
         но человек уже понял, что вышел */
    }
    setUser(null)
  }, [])

  return <AuthContext.Provider value={{ user, ready, loginEnabled, refresh, logout }}>{children}</AuthContext.Provider>
}

export const useAuth = () => useContext(AuthContext)
